import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import { getGifts } from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * Spread chooser — landing screen for all extended spreads.
 *
 * Previously the Home screen had a single "Extended spread" button
 * that hard-routed to /reading/celtic_cross, leaving `three_card`
 * and `love` completely unreachable from the UI. This screen surfaces
 * the full catalogue.
 */

interface SpreadCard {
  /** Path the card routes to (passed verbatim into <Link to={...}>). */
  href: string;
  /** Matches `transactions.product_id` — used to detect unconsumed gifts. */
  product_id?: string;
  icon: string;
  /** i18n key suffix for spread.<key>: short title shown big */
  title_key: string;
  /** i18n key for the marketing blurb under the title */
  blurb_key: string;
  /** Stars price for free users (0 if it's not a Stars purchase) */
  stars: number;
  /** Approximate read time in minutes (for UI hint) */
  minutes: number;
  /** When true, the card is locked behind the Priestess subscription. */
  priestess_only?: boolean;
}

const SPREADS: SpreadCard[] = [
  {
    href: '/reading/three_card',
    product_id: 'spread_three_card',
    icon: '⁂',
    title_key: 'three_card',
    blurb_key: 'three_card_blurb',
    stars: 25,
    minutes: 3,
  },
  {
    href: '/reading/celtic_cross',
    product_id: 'spread_celtic_cross',
    icon: '✦',
    title_key: 'celtic_cross',
    blurb_key: 'celtic_cross_blurb',
    stars: 50,
    minutes: 8,
  },
  {
    href: '/reading/love',
    product_id: 'spread_love',
    icon: '♡',
    title_key: 'love',
    blurb_key: 'love_blurb',
    stars: 50,
    minutes: 5,
  },
  {
    href: '/dream',
    product_id: 'dream_reading',
    icon: '☾',
    title_key: 'dream',
    blurb_key: 'dream_blurb',
    stars: 25,
    minutes: 4,
  },
  {
    href: '/themed',
    product_id: 'themed_reading',
    icon: '🎯',
    title_key: 'themed',
    blurb_key: 'themed_blurb',
    stars: 25,
    minutes: 4,
  },
  {
    href: '/numerology',
    product_id: 'numerology_report',
    icon: '🔢',
    title_key: 'numerology',
    blurb_key: 'numerology_blurb',
    stars: 100,
    minutes: 10,
  },
  // ── New spreads — popular layouts pack ──────────────────────────────
  {
    href: '/reading/horseshoe',
    product_id: 'spread_horseshoe',
    icon: '🃏',
    title_key: 'horseshoe',
    blurb_key: 'horseshoe_blurb',
    stars: 25,
    minutes: 5,
  },
  {
    href: '/reading/magus_star',
    product_id: 'spread_magus_star',
    icon: '🔮',
    title_key: 'magus_star',
    blurb_key: 'magus_star_blurb',
    stars: 30,
    minutes: 6,
  },
  {
    href: '/reading/wedding',
    product_id: 'spread_wedding',
    icon: '💍',
    title_key: 'wedding',
    blurb_key: 'wedding_blurb',
    stars: 40,
    minutes: 8,
  },
  {
    href: '/reading/home',
    product_id: 'spread_home',
    icon: '🏠',
    title_key: 'home',
    blurb_key: 'home_blurb',
    stars: 25,
    minutes: 4,
  },
  {
    href: '/reading/masks',
    product_id: 'spread_masks',
    icon: '🎭',
    title_key: 'masks',
    blurb_key: 'masks_blurb',
    stars: 25,
    minutes: 4,
  },
  {
    href: '/reading/wallet',
    product_id: 'spread_wallet',
    icon: '💸',
    title_key: 'wallet',
    blurb_key: 'wallet_blurb',
    stars: 25,
    minutes: 4,
  },
  {
    href: '/reading/shadow',
    product_id: 'spread_shadow',
    icon: '🌑',
    title_key: 'shadow',
    blurb_key: 'shadow_blurb',
    stars: 30,
    minutes: 6,
  },
  {
    href: '/reading/crossroads',
    product_id: 'spread_crossroads',
    icon: '🚪',
    title_key: 'crossroads',
    blurb_key: 'crossroads_blurb',
    stars: 25,
    minutes: 5,
  },
  {
    href: '/weekly',
    icon: '✦',
    title_key: 'weekly',
    blurb_key: 'weekly_blurb',
    stars: 0,        // priestess-only — display handled below
    minutes: 5,
    priestess_only: true,
  },
];

export default function Spreads() {
  const { t } = useTranslation();
  const { isPriestess } = useSubscription();

  // Unconsumed `completed` transactions — onboarding bonus, referral
  // gifts, milestone rewards all show up here.
  const [gifts, setGifts] = useState<Set<string>>(new Set());
  useEffect(() => {
    getGifts()
      .then((g) => setGifts(new Set(g.products)))
      .catch(() => {});
  }, []);

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-1 text-center font-serif text-2xl text-gold-300">
        {t('spreads.title')}
      </h1>
      <p className="mb-6 text-center text-xs italic text-gold-300/60">
        {t('spreads.subtitle')}
      </p>

      <div className="space-y-3">
        {SPREADS.map((s, i) => (
          <motion.div
            key={s.href}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
          >
            <Link
              to={s.href}
              className="block transition-transform active:scale-[0.99]"
            >
              <OrnateFrame className="card-glow hover:border-gold-400/70">
                <div className="flex items-start gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center
                                  rounded-full border border-gold-500/40 bg-night-900/60
                                  font-serif text-3xl text-gold-300">
                    {s.icon}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h2 className="font-serif text-lg leading-tight text-gold-200">
                      {t(`spread.${s.title_key}`)}
                    </h2>
                    <p className="mt-1 text-xs italic leading-snug text-gold-300/70">
                      {t(`spread.${s.blurb_key}`)}
                    </p>

                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-widest text-gold-500/60">
                        {t('spreads.time_minutes', { count: s.minutes })}
                      </span>
                      {(() => {
                        // Status badge — order matters:
                        //   1. Priestess-only spread: always shows the
                        //      crown locked label.
                        //   2. Active subscription + gift: subscription
                        //      will open it, gift stays — label says
                        //      "gift on hold" so the user knows.
                        //   3. Subscription only: "included in Priestess".
                        //   4. Gift only (no sub): "gift available".
                        //   5. Nothing: paid-stars price.
                        const hasGift = !!(s.product_id && gifts.has(s.product_id));
                        let icon: string, label: string, color: string;
                        if (s.priestess_only) {
                          icon = '👑'; label = t('spread.priestess_only'); color = 'text-gold-400';
                        } else if (isPriestess && hasGift) {
                          icon = '🎁'; label = t('spread.gift_in_reserve'); color = 'text-emerald-300/80';
                        } else if (hasGift) {
                          icon = '🎁'; label = t('spread.gift_available'); color = 'text-emerald-300';
                        } else if (isPriestess) {
                          icon = '✦'; label = t('spread.included_priestess'); color = 'text-gold-400';
                        } else {
                          icon = ''; label = `${s.stars} ⭐`; color = 'text-gold-300';
                        }
                        return (
                          <span className={`text-[11px] font-semibold ${color}`}>
                            {icon ? `${icon} ${label}` : label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </OrnateFrame>
            </Link>
          </motion.div>
        ))}
      </div>

      <p className="mt-6 text-center text-[10px] uppercase tracking-widest text-gold-500/40">
        {t('spreads.more_soon')}
      </p>
    </main>
  );
}
